import OrganismSearch from "@/components/common/OrganismSearch";
import AlignmentToolSelect from "@/components/common/AlignmentToolSelect";
import MetadataBuilder from "@/components/common/MetadataBuilder";
import ComparisonBuilder from "@/components/common/ComparisonBuilder";
const MIN_PHRED_QUALITY_SCORE = 20;
const MAX_PHRED_QUALITY_SCORE = 40;

export const defaultValues = {
  organism: null,
  alignment_tool: "",
  read_type: "single",
  sample_metadata: [{ sample_id: "", ip: "", condition: "" }],
  comparisons: {
    auto_generate: false,
    saved: false,
    items: [],
  },
  metadata_locked: false,
  min_read_length: 36,
  phred_quality_score: 30,
  peak_type: "narrow",
  significance_threshold_type: "p-value",
  significance_threshold_value: 0.00001,
  motif_database: "JASPAR CORE (2024)",
  motif_significance_threshold: 0.05,
};

export const validate = (values = {}) => {
  const errors = {};

  if (!values.organism) {
    errors.organism = "Organism is required.";
  }
  if (!String(values.alignment_tool || "").trim()) {
    errors.alignment_tool = "Alignment tool is required.";
  }
  if (!String(values.read_type || "").trim()) {
    errors.read_type = "Read type is required.";
  }

  const metadataRows = Array.isArray(values.sample_metadata) ? values.sample_metadata : [];
  const metadataErrors = metadataRows.map((row = {}) => {
    const rowErrors = {};
    if (!String(row.sample_id || "").trim()) {
      rowErrors.sample_id = "Sample ID is required.";
    }

    if (!String(row.ip || "").trim()) {
      rowErrors.ip = "IP is required.";
    }

    if (
      row.ip === "IP" &&
      !String(row.condition || "").trim()
    ) {
      rowErrors.condition = "Condition is required.";
    }

    return rowErrors;
  });

  const iggCount = metadataRows.filter(
    (row) => row.ip === "IgG / Genomic Input"
  ).length;

  if (iggCount > 1) {
    errors.sample_metadata_message =
      "Only one IgG / Genomic Input sample is allowed.";
  }

  const comparisonConditions = [
    ...new Set(
      metadataRows
        .filter(
          (row) =>
            row.ip === "IP" &&
            row.condition?.trim()
        )
        .map((row) => row.condition.trim())
    ),
  ];

  if (comparisonConditions.length >= 2) {
    const comparisons = values.comparisons || {};

    if (!comparisons.auto_generate && !comparisons.saved) {
      errors.comparisons =
        "Please configure and save the comparisons.";
    }
  }

  metadataErrors.forEach((rowErrors, index) => {
    Object.entries(rowErrors).forEach(([key, message]) => {
      errors[`${key}_${index}`] = message;
    });
  });

  const uniqueConditions = [
    ...new Set(
      metadataRows
        .filter(
          (row) =>
            row.ip === "IP" &&
            row.condition?.trim()
        )
        .map((row) => row.condition.trim())
    ),
  ];

  if (
    uniqueConditions.length >= 2 &&
    !values.comparisons?.auto_generate &&
    !values.comparisons?.saved
  ) {
    errors.comparisons =
      "Please save the comparisons.";
  }

  if (!String(values.min_read_length || "").trim()) {
    errors.min_read_length = "Minimum read length is required.";
  }
  if (!String(values.phred_quality_score || "").trim()) {
    errors.phred_quality_score = "Phred quality score is required.";
  } else {
    const numericValue = Number(values.phred_quality_score);
    if (Number.isNaN(numericValue) || numericValue < MIN_PHRED_QUALITY_SCORE || numericValue > MAX_PHRED_QUALITY_SCORE) {
      errors.phred_quality_score = `Value must be between ${MIN_PHRED_QUALITY_SCORE} and ${MAX_PHRED_QUALITY_SCORE}.`;
    }
  }

  if (!String(values.peak_type || "").trim()) {
    errors.peak_type = "Peak type is required.";
  }
  if (!String(values.significance_threshold_type || "").trim()) {
    errors.significance_threshold_type = "Significance threshold type is required.";
  } else if (!String(values.significance_threshold_value || "").trim()) {
    errors.significance_threshold_value = "Threshold value is required.";
  }
  if (!String(values.motif_database || "").trim()) {
    errors.motif_database = "Motif database is required.";
  }
  if (!String(values.motif_significance_threshold || "").trim()) {
    errors.motif_significance_threshold = "Significance threshold is required.";
  }

  return errors;
};

export default function ChIPSeqParameters({
  values = {},
  onChange,
  errors = {},
  expectedSamples,
}) {
  const setValue = (key, value) => onChange(key, value);

  const normalizePhredScore = (value) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return "";
    const numericValue = Number(trimmed);
    if (Number.isNaN(numericValue)) return "";
    return String(Math.min(Math.max(numericValue, MIN_PHRED_QUALITY_SCORE), MAX_PHRED_QUALITY_SCORE));
  };

  const comparisonMetadata =
    (values.sample_metadata || []).filter(
      (row) =>
        row.ip === "IP" &&
        row.condition?.trim()
    );

  const uniqueConditions = [
    ...new Set(
      comparisonMetadata.map((row) => row.condition.trim())
    ),
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <h3 className="text-sm font-semibold text-[#09582D]">Basic Analysis Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Organism
            </label>

            <OrganismSearch
              value={values.organism}
              onChange={(organism) => setValue("organism", organism)}
            />

            {errors.organism && (
              <p className="mt-1 text-xs text-red-600">{errors.organism}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Alignment Tool
            </label>

            <AlignmentToolSelect
              value={values.alignment_tool || ""}
              onChange={(value) => setValue("alignment_tool", value)}
              options={["BWA", "Bowtie", "Bowtie2"]}
            />

            {errors.alignment_tool && (
              <p className="mt-1 text-xs text-red-600">
                {errors.alignment_tool}
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-2">Read Type</label>
            <div className="flex flex-wrap gap-4">
              {[
                { value: "single", label: "Single End" },
                { value: "paired", label: "Paired End" },
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="chipseq-read-type"
                    value={option.value}
                    checked={values.read_type === option.value}
                    onChange={(e) => setValue("read_type", e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {errors.read_type && <p className="mt-1 text-xs text-red-600">{errors.read_type}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <MetadataBuilder
          value={values.sample_metadata}
          onChange={(rows) => setValue("sample_metadata", rows)}
          expectedSamples={expectedSamples}
          onLockChange={(locked) =>
            setValue("metadata_locked", locked)
          }
          columns={[
            {
              key: "sample_id",
              label: "Sample ID",
            },

            {
              key: "ip",
              label: "IP",
              type: "select",
              options: [
                "IP",
                "IgG / Genomic Input",
              ],
              required: (row) => !!row.ip,
              requiredMessage: "Please select IP for all samples.",

              optionDisabled: (option, rows, currentIndex) =>
                option === "IgG / Genomic Input" &&
                rows.some(
                  (r, i) =>
                    i !== currentIndex &&
                    r.ip === "IgG / Genomic Input"
                ),

              onChange: (row, value) => {
                if (value === "IgG / Genomic Input") {
                  row.condition = "";
                }
              },
            },

            {
              key: "condition",
              label: "Condition",

              disabled: (row) =>
                row.ip === "IgG / Genomic Input",

              required: (row) =>
                row.ip === "IgG / Genomic Input" || !!row.condition?.trim(),

              requiredMessage:
                "Condition is required for all IP samples.",
            },
          ]}
        />
      </div>


      <div className="rounded-lg border border-[#E5E7EB] p-4">
        {uniqueConditions.length >= 2 ? (
          <ComparisonBuilder
            metadata={comparisonMetadata}
            value={values.comparisons || {}}
            onChange={(comparison) =>
              setValue("comparisons", comparison)
            }
            errors={errors}
          />
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <h3 className="text-sm font-semibold text-amber-800">
              Comparison Builder
            </h3>

            <p className="mt-2 text-sm text-amber-700">
              Add at least two unique experimental conditions (IP samples) in the
              Metadata Builder to enable comparisons.
            </p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Raw Data QC Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phred Quality Score</label>
            <input
              type="number"
              min={MIN_PHRED_QUALITY_SCORE}
              max={MAX_PHRED_QUALITY_SCORE}
              value={values.phred_quality_score ?? ""}
              onChange={(e) => setValue("phred_quality_score", normalizePhredScore(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_PHRED_QUALITY_SCORE} to {MAX_PHRED_QUALITY_SCORE}</p>
            {errors.phred_quality_score && <p className="mt-1 text-xs text-red-600">{errors.phred_quality_score}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Minimum Read Length</label>
            <input
              type="number"
              min="1"
              value={values.min_read_length ?? ""}
              onChange={(e) => setValue("min_read_length", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            {errors.min_read_length && <p className="mt-1 text-xs text-red-600">{errors.min_read_length}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">
          Peak Calling Parameters
        </h3>

        <div className="mt-4 grid gap-6 md:grid-cols-2">

          {/* Peak Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Peak Type
            </label>

            <div className="space-y-2">
              {[
                { value: "narrow", label: "Narrow" },
                { value: "broad", label: "Broad" },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="radio"
                    name="chipseq-peak-type"
                    value={option.value}
                    checked={values.peak_type === option.value}
                    onChange={(e) => setValue("peak_type", e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>

            {errors.peak_type && (
              <p className="mt-1 text-xs text-red-600">
                {errors.peak_type}
              </p>
            )}
          </div>

          {/* Significance Threshold */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Significance Threshold
            </label>

            <div className="flex gap-4">
              {[
                { value: "p-value", label: "p-value" },
                { value: "q-value", label: "q-value" },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="radio"
                    name="chipseq-threshold-type"
                    value={option.value}
                    checked={values.significance_threshold_type === option.value}
                    onChange={(e) =>
                      setValue("significance_threshold_type", e.target.value)
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>

            {errors.significance_threshold_type && (
              <p className="mt-1 text-xs text-red-600">
                {errors.significance_threshold_type}
              </p>
            )}

            {values.significance_threshold_type && (
              <div className="mt-4">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Threshold Value
                </label>

                <input
                  type="number"
                  step="0.0001"
                  value={values.significance_threshold_value || ""}
                  onChange={(e) =>
                    setValue("significance_threshold_value", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
                />

                {errors.significance_threshold_value && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.significance_threshold_value}
                  </p>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Motif Enrichment Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Select Motif Database</label>
            <select
              value={values.motif_database || ""}
              onChange={(e) => setValue("motif_database", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            >
              <option value="">Select one</option>

              <option>JASPAR CORE (2024)</option>
              <option>JASPAR CORE (2024) Vertebrates</option>
              <option>JASPAR CORE (2024) Plants</option>
              <option>JASPAR CORE (2024) Fungi</option>
              <option>JASPAR CORE (2024) Nematodes</option>
              <option>JASPAR CORE (2024) Insects</option>

              <option>HOCOMOCO (v12 Core)</option>
              <option>HOCOMOCO Human (v12 Core)</option>
              <option>HOCOMOCO Mouse (v12 Core)</option>

              <option>SwissRegulon Yeast</option>

              <option>UniPROBE Worm (Cell09)</option>
              <option>UniPROBE Yeast (GR09)</option>

              <option>YEASTRACT</option>

              <option>DAP Motifs (O'Malley 2016)</option>

              <option>PBM Motifs (Franco-Zorrilla 2014)</option>

              <option>Combined Drosophila Motif Databases</option>
            </select>
            {errors.motif_database && <p className="mt-1 text-xs text-red-600">{errors.motif_database}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Significance Threshold (p-value)</label>
            <input
              type="number"
              step="0.0001"
              value={values.motif_significance_threshold || ""}
              onChange={(e) => setValue("motif_significance_threshold", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            {errors.motif_significance_threshold && <p className="mt-1 text-xs text-red-600">{errors.motif_significance_threshold}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
